import { browserTimeZone } from '@/shared/lib/timeZones';
import {
  addCalendar,
  fromWallClock,
  isKnownTimeZone,
  startOfZonedDay,
  toWallClock,
} from '@/shared/lib/zonedTime';

/*
 * The "when it changed" filter of the search. The server filters on `updatedAt` between two instants (`from` inclusive, `to`
 * exclusive); the user picks a preset ("last 24 hours") or a range of calendar days. Everything is counted on the calendar
 * of the user's time zone — "a week ago" is the same wall clock seven days earlier, a custom day runs from its local
 * midnight to the next one, whatever the clocks did in between — never as a number of milliseconds.
 */

/** Rolling presets, each ending now. */
export const PERIOD_PRESETS = ['day', 'week', 'month', 'year'] as const;
export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

/** What the period control holds: a preset, or a range of days (`custom`, with `from`/`to` dates). */
export type PeriodSelection = PeriodPreset | 'custom';

/** The period state kept in the URL (`period`, and `from`/`to` as `YYYY-MM-DD` for a custom range). */
export interface SearchPeriod {
  period: PeriodSelection | null;
  from: string | null;
  to: string | null;
}

/** The instants the API takes (ISO strings; null = unbounded). */
export interface SearchRange {
  from: string | null;
  to: string | null;
}

export const NO_PERIOD: SearchPeriod = { period: null, from: null, to: null };
export const NO_RANGE: SearchRange = { from: null, to: null };

export function parsePeriod(value: string | null | undefined): PeriodSelection | null {
  return value === 'custom' || (PERIOD_PRESETS as readonly string[]).includes(value ?? '')
    ? (value as PeriodSelection)
    : null;
}

/** The user's zone, or the browser's when the profile's is unknown to this runtime. */
export function effectiveTimeZone(timeZone: string | null | undefined): string {
  return isKnownTimeZone(timeZone) ? timeZone : browserTimeZone();
}

/**
 * The instants of a period. A preset starts the same wall clock one day / seven days / thirty days / a year earlier than
 * `now` (rounded down to the minute, so the request does not change every render) and has no end; a custom range starts
 * at the local midnight of its first day and ends at the local midnight after its last (either may be missing or
 * malformed: that side is then open).
 */
export function resolveRange(selection: SearchPeriod, now: Date, timeZone: string): SearchRange {
  const zone = effectiveTimeZone(timeZone);
  if (selection.period === 'custom') {
    return {
      from: startOfZonedDay(selection.from ?? '', zone)?.toISOString() ?? null,
      to: startOfZonedDay(selection.to ?? '', zone, 1)?.toISOString() ?? null,
    };
  }
  if (!selection.period) return NO_RANGE;
  const minute = new Date(Math.floor(now.getTime() / 60_000) * 60_000);
  const wall = toWallClock(minute, zone);
  const back = {
    day: { days: -1 },
    week: { days: -7 },
    month: { days: -30 },
    year: { years: -1 },
  }[selection.period];
  return { from: fromWallClock(addCalendar({ ...wall, second: 0 }, back), zone).toISOString(), to: null };
}

/** Whether a period narrows anything. */
export const hasPeriod = (selection: SearchPeriod): boolean =>
  selection.period !== null && (selection.period !== 'custom' || !!selection.from || !!selection.to);
